import { useState } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { Link } from "react-router-dom";
import { ChevronLeft, Sparkles } from "lucide-react";
import PitchView from "../../components/football/PitchView";
import { hapticLight } from "../../utils/haptics";

// Static seeded player pool (preview only — no backend).
const POOL = {
  GK: [{ player_id: "gk1", name: "Emiliano Martinez", team_name: "Argentina", team_id: 26, jersey_number: 23, credits: 8.0 }],
  DEF: [
    { player_id: "d1", name: "Cristian Romero", team_name: "Argentina", team_id: 26, jersey_number: 13, credits: 7.5 },
    { player_id: "d2", name: "Nicolas Otamendi", team_name: "Argentina", team_id: 26, jersey_number: 19, credits: 7.0 },
    { player_id: "d3", name: "Marquinhos", team_name: "Brazil", team_id: 6, jersey_number: 4, credits: 7.5 },
    { player_id: "d4", name: "Theo Hernandez", team_name: "France", team_id: 2, jersey_number: 22, credits: 8.0 },
    { player_id: "d5", name: "Ruben Dias", team_name: "Portugal", team_id: 27, jersey_number: 3, credits: 7.5 },
  ],
  MID: [
    { player_id: "m1", name: "Rodri", team_name: "Spain", team_id: 9, jersey_number: 16, credits: 9.0 },
    { player_id: "m2", name: "Jude Bellingham", team_name: "England", team_id: 10, jersey_number: 10, credits: 10.5 },
    { player_id: "m3", name: "Vinicius Junior", team_name: "Brazil", team_id: 6, jersey_number: 7, credits: 11.0 },
    { player_id: "m4", name: "Pedri", team_name: "Spain", team_id: 9, jersey_number: 8, credits: 8.5 },
    { player_id: "m5", name: "Federico Valverde", team_name: "Uruguay", team_id: 7, jersey_number: 15, credits: 8.5 },
  ],
  FWD: [
    { player_id: "f1", name: "Lionel Messi", team_name: "Argentina", team_id: 26, jersey_number: 10, credits: 12.0 },
    { player_id: "f2", name: "Kylian Mbappe", team_name: "France", team_id: 2, jersey_number: 10, credits: 12.0 },
    { player_id: "f3", name: "Erling Haaland", team_name: "Norway", team_id: 99, jersey_number: 9, credits: 11.5 },
  ],
};

const FORMATIONS = {
  "4-4-2": { GK: 1, DEF: 4, MID: 4, FWD: 2 },
  "3-5-2": { GK: 1, DEF: 3, MID: 5, FWD: 2 },
  "4-3-3": { GK: 1, DEF: 4, MID: 3, FWD: 3 },
  "3-4-3": { GK: 1, DEF: 3, MID: 4, FWD: 3 },
};

const DEMO_POINTS = {
  f1: 20, f2: 9, f3: 6, m1: 5, m2: 8, m3: 12, m4: 5, m5: 2,
  d1: 6, d2: 2, d3: 6, d4: 9, d5: 6, gk1: 7,
};

function buildSquad(formationKey, captainId, vcId, withPoints) {
  const f = FORMATIONS[formationKey];
  const squad = [];
  ["GK", "DEF", "MID", "FWD"].forEach((pos) => {
    POOL[pos].slice(0, f[pos]).forEach((p) => {
      squad.push({
        ...p,
        position: pos,
        is_captain: p.player_id === captainId,
        is_vice_captain: p.player_id === vcId,
        is_super_sub: false,
        points_earned: withPoints ? (DEMO_POINTS[p.player_id] || 0) : 0,
      });
    });
  });
  // one super sub for the bench demo
  squad.push({
    player_id: "ss1", name: "Lautaro Martinez", team_name: "Argentina", team_id: 26,
    jersey_number: 22, credits: 9.5, position: "FWD", is_super_sub: true,
    points_earned: withPoints ? 5 : 0,
  });
  return squad;
}

export default function FootballPitchPreviewPage() {
  const [formation, setFormation] = useState("4-4-2");
  const [captainId, setCaptainId] = useState("f1");
  const [vcId, setVcId] = useState("m3");
  const [showPoints, setShowPoints] = useState(false);

  const squad = buildSquad(formation, captainId, vcId, showPoints);

  const handleSlotPress = (slot) => {
    hapticLight();
    if (slot.is_super_sub) return;
    // tap cycles captain -> vc -> none for demo
    if (slot.player_id === captainId) { setCaptainId(null); setVcId(slot.player_id); }
    else { setVcId(captainId); setCaptainId(slot.player_id); }
  };

  return (
    <div className="football-theme min-h-screen bg-background pb-24">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-6 pt-5">
        <Link to="/" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-white/80" data-testid="pitch-preview-back">
          <ChevronLeft size={18} /> Back
        </Link>
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-amber-300" />
          <h1 className="text-xl font-extrabold text-white">PitchView Preview</h1>
        </div>
        <p className="mt-1 text-sm text-white/70">Football World Cup 2026 · premium pitch surface</p>
      </div>

      <div className="mx-auto max-w-md px-4 pt-4">
        {/* Controls */}
        <div className="mb-4 space-y-3 rounded-xl border border-border bg-card/60 p-3">
          <div>
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Formation (tap to transition)</div>
            <div className="flex gap-2">
              {Object.keys(FORMATIONS).map((key) => (
                <button
                  key={key}
                  onClick={() => { hapticLight(); setFormation(key); }}
                  className={`flex-1 rounded-lg px-2 py-2 text-sm font-bold transition-colors ${
                    formation === key ? "wc-bg-gold text-slate-900" : "bg-secondary text-foreground"
                  }`}
                  data-testid={`formation-${key}`}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => { hapticLight(); setShowPoints((v) => !v); }}
            className="w-full rounded-lg bg-secondary py-2.5 text-sm font-bold text-foreground"
            data-testid="toggle-points-reveal"
          >
            {showPoints ? "Hide points" : "Reveal live points ✨"}
          </button>
          <p className="text-center text-xs text-muted-foreground">Tap a player to set Captain / Vice-Captain</p>
        </div>

        <PitchView squad={squad} showPoints={showPoints} onSlotPress={handleSlotPress} />
      </div>
    </div>
  );
}
